// utils/improvedRake.js
import nlp from 'compromise';

/**
 * Extracts meaningful keywords and phrases using an improved RAKE approach.
 * @param {string} text - The input text.
 * @returns {string[]} - Top keywords and phrases.
 */
export const extractKeywords = (text) => {
  if (!text) return [];

  // More comprehensive stopwords list
  const stopwords = new Set([
    'a', 'about', 'above', 'after', 'again', 'against', 'all', 'am', 'an', 'and', 
    'any', 'are', 'aren\'t', 'as', 'at', 'be', 'because', 'been', 'before', 'being', 
    'below', 'between', 'both', 'but', 'by', 'can', 'can\'t', 'cannot', 'could', 
    'couldn\'t', 'did', 'didn\'t', 'do', 'does', 'doesn\'t', 'doing', 'don\'t', 'down', 
    'during', 'each', 'few', 'for', 'from', 'further', 'had', 'hadn\'t', 'has', 'hasn\'t', 
    'have', 'haven\'t', 'having', 'he', 'he\'d', 'he\'ll', 'he\'s', 'her', 'here', 'here\'s', 
    'hers', 'herself', 'him', 'himself', 'his', 'how', 'how\'s', 'i', 'i\'d', 'i\'ll', 'i\'m', 
    'i\'ve', 'if', 'in', 'into', 'is', 'isn\'t', 'it', 'it\'s', 'its', 'itself', 'let\'s', 'me', 
    'more', 'most', 'mustn\'t', 'my', 'myself', 'no', 'nor', 'not', 'of', 'off', 'on', 'once', 
    'only', 'or', 'other', 'ought', 'our', 'ours', 'ourselves', 'out', 'over', 'own', 'same', 
    'shan\'t', 'she', 'she\'d', 'she\'ll', 'she\'s', 'should', 'shouldn\'t', 'so', 'some', 'such', 
    'than', 'that', 'that\'s', 'the', 'their', 'theirs', 'them', 'themselves', 'then', 'there', 
    'there\'s', 'these', 'they', 'they\'d', 'they\'ll', 'they\'re', 'they\'ve', 'this', 'those', 
    'through', 'to', 'too', 'under', 'until', 'up', 'very', 'was', 'wasn\'t', 'we', 'we\'d', 
    'we\'ll', 'we\'re', 'we\'ve', 'were', 'weren\'t', 'what', 'what\'s', 'when', 'when\'s', 
    'where', 'where\'s', 'which', 'while', 'who', 'who\'s', 'whom', 'why', 'why\'s', 'with', 
    'won\'t', 'would', 'wouldn\'t', 'you', 'you\'d', 'you\'ll', 'you\'re', 'you\'ve', 'your', 
    'yours', 'yourself', 'yourselves'
  ]);

  // Clean text and split into sentences
  const cleanText = text.toLowerCase()
    .replace(/[^\w\s.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  
  // Step 1: Extract candidate phrases (words separated by stopwords)
  const sentences = cleanText.split(/[.!?]+/).filter(s => s.trim().length > 0);
  
  // Extract phrases from each sentence
  const candidatePhrases = [];
  sentences.forEach(sentence => {
    // Replace stopwords with a marker
    const markedSentence = sentence.split(' ')
      .map(word => stopwords.has(word) ? '|' : word)
      .join(' ');
    
    // Split by the marker to get phrases
    const phrases = markedSentence.split('|')
      .map(phrase => phrase.trim())
      .filter(phrase => phrase.length > 0);
    
    // Add meaningful phrases (more than one word or a single significant word)
    phrases.forEach(phrase => {
      const words = phrase.split(' ').filter(w => w.length > 0);
      if (words.length > 1 || (words.length === 1 && words[0].length > 3)) {
        candidatePhrases.push(phrase);
      }
    });
  });

  // Step 2: Calculate word scores
  const wordFrequency = new Map();
  const wordDegree = new Map();
  
  candidatePhrases.forEach(phrase => {
    const words = phrase.split(' ').filter(w => w.length > 0);
    
    // For each word in the phrase
    words.forEach(word => {
      // Update word frequency
      wordFrequency.set(word, (wordFrequency.get(word) || 0) + 1);
      
      // Update word degree (words it appears with)
      wordDegree.set(word, (wordDegree.get(word) || 0) + words.length - 1);
    });
  });

  // Calculate word scores
  const wordScores = new Map();
  wordFrequency.forEach((freq, word) => {
    const degree = wordDegree.get(word);
    wordScores.set(word, degree / freq);
  });

  // Step 3: Calculate phrase scores
  const phraseScores = new Map();
  candidatePhrases.forEach(phrase => {
    const words = phrase.split(' ').filter(w => w.length > 0);
    let score = 0;
    
    words.forEach(word => {
      score += wordScores.get(word) || 0;
    });
    
    // Boost score for multi-word phrases (likely more meaningful)
    if (words.length > 1) {
      score *= 1.5;
    }
    
    phraseScores.set(phrase, score);
  });

  // Step 4: Get noun phrases using compromise NLP
  const doc = nlp(text);
  const nounPhrases = doc.match('#Noun+').out('array');
  
  // Add these to our candidates with a boost
  nounPhrases.forEach(phrase => {
    const cleanPhrase = phrase.toLowerCase().trim();
    if (cleanPhrase.length > 3 && !stopwords.has(cleanPhrase)) {
      phraseScores.set(cleanPhrase, (phraseScores.get(cleanPhrase) || 0) + 3);
    }
  });

  // Step 5: Sort phrases by score and return top phrases
  const sortedPhrases = Array.from(phraseScores.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([phrase]) => phrase);

  // Return top 15 keywords/phrases
  return sortedPhrases.slice(0, 15);
};